/**
 * Customer-facing slot confirmation page.
 *
 * Lives on per-sede domains (verdevercelli.it / verdevalsesia.it / …). The
 * backend builds confirmation links as `https://<sede-domain>/conferma/<token>`
 * (see landing/api_views.py _send_proposal_email + propose_slots), so this
 * page is the verdevercelli.it side of that contract.
 *
 * Same pattern as SharePreventivoPage: the call to
 * `/api/landing/confirm/<token>/` is same-origin from the browser's POV; the
 * sede's nginx reverse-proxies it to `https://avmg.it/...`. No CORS, no
 * cross-origin cookie surface, the brand experience stays on the sede domain.
 */
import { useEffect, useState } from 'react'
import type { SedeConfig } from '../sedi'

interface SlotInfo {
  num: number
  date: string
  time: string
  label: string
}

interface ProposalData {
  name: string
  service_type: string
  address: string
  slots: SlotInfo[]
  is_expired: boolean
  is_confirmed: boolean
  selected_slot: number | null
  confirmed_at: string | null
  confirmed_label?: string
}

function readSlotParam(): number | null {
  if (typeof window === 'undefined') return null
  const p = new URLSearchParams(window.location.search).get('slot')
  if (!p) return null
  const n = parseInt(p, 10)
  return [1, 2, 3].includes(n) ? n : null
}

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

export default function ConfirmSlotPage({ token, sede }: { token: string; sede: SedeConfig }) {
  const preselected = readSlotParam()

  const [data, setData] = useState<ProposalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'expired' | 'not_found' | 'generic' | ''>('')
  const [confirming, setConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [confirmedLabel, setConfirmedLabel] = useState('')
  const [selectedSlot, setSelectedSlot] = useState<number | null>(preselected)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    fetch(`/api/landing/confirm/${token}/`, { credentials: 'omit' })
      .then(async r => {
        if (cancelled) return
        if (r.status === 410) { setError('expired'); return }
        if (r.status === 404) { setError('not_found'); return }
        if (!r.ok) { setError('generic'); return }
        const d = (await r.json()) as ProposalData
        setData(d)
        // GET returns 200 even when the proposal is expired — the 410 is
        // only thrown on POST. Treat is_expired as terminal so the user
        // sees the "scaduto" screen instead of an inert slot picker that
        // will fail when they click confirm.
        if (d.is_expired && !d.is_confirmed) {
          setError('expired')
          return
        }
        if (d.is_confirmed) {
          setConfirmed(true)
          setConfirmedLabel(d.confirmed_label || '')
          setSelectedSlot(d.selected_slot)
        }
      })
      .catch(() => { if (!cancelled) setError('generic') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [token])

  const handleConfirm = async () => {
    if (!selectedSlot || !token) return
    setConfirming(true)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      const csrf = readCsrfCookie()
      if (csrf) headers['X-CSRFToken'] = csrf
      const res = await fetch(`/api/landing/confirm/${token}/`, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify({ slot: selectedSlot }),
      })
      if (res.status === 410) { setError('expired'); return }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}))
        if (typeof body?.detail === 'string' && body.detail.includes('già stata confermata')) {
          setConfirmed(true)
          // Backend echoes the already-confirmed label on this 400 path,
          // so reuse it rather than leaving the success screen blank.
          if (typeof body?.confirmed_label === 'string') {
            setConfirmedLabel(body.confirmed_label)
          }
          return
        }
        setError('generic')
        return
      }
      if (!res.ok) { setError('generic'); return }
      const body = await res.json()
      setConfirmed(true)
      setConfirmedLabel(body?.confirmed_label || '')
    } catch {
      setError('generic')
    } finally {
      setConfirming(false)
    }
  }

  const brandName = sede.brand.name
  const brandPhone = sede.contact.phone
  const brandEmail = sede.contact.email
  const brandFooter = `${brandName} — ${sede.brand.tagline} · ${sede.contact.address}`

  const telHref = brandPhone ? `tel:${brandPhone.replace(/\s+/g, '')}` : undefined

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex flex-col">
      <header className="bg-[#013220] px-6 py-8 text-center">
        <h1 className="text-2xl font-light text-white tracking-[3px]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
          {brandName}
        </h1>
        <p className="text-[11px] text-white/50 tracking-[3px] uppercase mt-2">
          Conferma Sopralluogo
        </p>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-lg">

          {loading && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#013220] border-t-transparent rounded-full mx-auto" />
              <p className="text-sm text-gray-400 mt-4">Caricamento...</p>
            </div>
          )}

          {error === 'expired' && (
            <div className="bg-white rounded-lg shadow-sm p-10 text-center">
              <p className="text-4xl mb-4">⏰</p>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Link scaduto</h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Questo link non è più valido. La preghiamo di contattarci per fissare un appuntamento.
              </p>
              <div className="mt-6 space-y-2">
                {telHref && (
                  <a href={telHref} className="block text-sm text-[#013220] font-semibold hover:underline">
                    {brandPhone}
                  </a>
                )}
                <a href={`mailto:${brandEmail}`} className="block text-sm text-[#013220] font-semibold hover:underline">
                  {brandEmail}
                </a>
              </div>
            </div>
          )}

          {error === 'not_found' && (
            <div className="bg-white rounded-lg shadow-sm p-10 text-center">
              <p className="text-4xl mb-4">🔍</p>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Pagina non trovata</h2>
              <p className="text-sm text-gray-500">Il link potrebbe non essere corretto.</p>
            </div>
          )}

          {error === 'generic' && (
            <div className="bg-white rounded-lg shadow-sm p-10 text-center">
              <p className="text-4xl mb-4">⚠️</p>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Si è verificato un errore</h2>
              <p className="text-sm text-gray-500">Riprovi più tardi o ci contatti direttamente.</p>
            </div>
          )}

          {!loading && !error && confirmed && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-green-50 border-b border-green-100 p-8 text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Sopralluogo confermato</h2>
                <p className="text-sm text-gray-500 mt-1">Grazie{data?.name ? `, ${data.name.split(' ')[0]}` : ''}!</p>
              </div>
              <div className="p-8 text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Data confermata</p>
                <p className="text-lg font-semibold text-[#013220]">{confirmedLabel}</p>
                <p className="text-sm text-gray-500 mt-6 leading-relaxed">
                  Riceverà un promemoria prima dell'appuntamento. Per qualsiasi modifica non esiti a contattarci.
                </p>
                <div className="mt-6 flex justify-center gap-6 flex-wrap">
                  {telHref && (
                    <a href={telHref} className="text-sm text-[#013220] font-medium hover:underline">
                      {brandPhone}
                    </a>
                  )}
                  <a href={`mailto:${brandEmail}`} className="text-sm text-[#013220] font-medium hover:underline">
                    {brandEmail}
                  </a>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && data && !confirmed && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-8 pb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Buongiorno{data.name ? ` ${data.name.split(' ')[0]}` : ''},
                </h2>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  Scelga la data che preferisce per il sopralluogo
                  {data.address ? ` presso ${data.address}` : ''}:
                </p>
              </div>

              <div className="px-8 pb-4 space-y-3">
                {data.slots.map(slot => (
                  <button
                    key={slot.num}
                    onClick={() => setSelectedSlot(slot.num)}
                    className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                      selectedSlot === slot.num
                        ? 'border-[#013220] bg-[#013220]/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[15px] font-semibold text-gray-900">{slot.label}</span>
                      {selectedSlot === slot.num && (
                        <div className="w-5 h-5 bg-[#013220] rounded-full flex items-center justify-center shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="px-8 pb-8 pt-4">
                <button
                  onClick={handleConfirm}
                  disabled={!selectedSlot || confirming}
                  className="w-full py-3.5 bg-[#013220] text-white font-semibold rounded-lg hover:bg-[#016432] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                >
                  {confirming ? 'Conferma in corso...' : 'Conferma data'}
                </button>
                <p className="text-xs text-gray-400 text-center mt-4">
                  Il link è valido per 24 ore dalla ricezione.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="bg-[#013220] px-6 py-5 text-center">
        <p className="text-xs text-white/40">{brandFooter}</p>
      </footer>
    </div>
  )
}
